// Verwaltung › Personen: Mitglieder mit Rollen je Standort, Einladungen, Sperren, „Was darf diese Person?“.
// Alle ändernden Verträge sind sicherheitsrelevant (effects.securitySensitive, frische Anmeldung) und dem
// KI-Assistenten vorenthalten (assistant: 'never'): Über den Assistenten vergibt niemand Zugänge oder Rechte.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

const zTimestamp = z.string()

export const MEMBER_STATUSES = ['active', 'disabled'] as const
export const MEMBER_STATUS_LABELS: Record<string, string> = { active: 'Aktiv', disabled: 'Gesperrt', invited: 'Eingeladen' }

/** Rolle an einem Standort; `locationId` null = an allen Standorten. */
export const zRoleGrant = z.strictObject({ roleId: zId, locationId: zId.nullable() })

export const zMemberRole = z.object({
  assignmentId: zId,
  roleId: zId,
  roleName: z.string(),
  /** null = alle Standorte */
  locationId: zId.nullable(),
  /** „F01 Getränkemarkt Garching“ bzw. „Alle Standorte“ */
  locationLabel: z.string(),
})

export const zMember = z.object({
  membershipId: zId,
  userId: zId,
  displayName: z.string(),
  /** bei technischen Zugängen die interne Adresse */
  email: z.string(),
  status: z.enum(['active', 'disabled', 'invited']),
  statusLabel: z.string(),
  /** technischer Zugang (Schnittstelle, Kassengerät): meldet sich nie an der Oberfläche an */
  technical: z.boolean(),
  lastLoginAt: zTimestamp.nullable(),
  defaultLocationId: zId.nullable(),
  /** laufende Sitzungen in diesem Betrieb */
  activeSessions: z.int().nonnegative(),
  roles: z.array(zMemberRole),
  /** die angemeldete Person selbst */
  isSelf: z.boolean(),
  /** hält alle Rechte an allen Standorten */
  isOwner: z.boolean(),
  /** Zwei-Faktor-Anmeldung eingerichtet (technische Zugänge: immer 'off') */
  mfa: z.enum(['on', 'off']),
  /** dieser Betrieb verlangt sie für die Person (Richtlinie oder Rolle) */
  mfaRequired: z.boolean(),
  /** Frist der Einrichtung (ISO) – nur solange Pflicht und nicht eingerichtet */
  mfaDueAt: zTimestamp.nullable(),
  /** verbliebene Wiederherstellungscodes (null ohne Zwei-Faktor) */
  recoveryCodesLeft: z.int().nonnegative().nullable(),
  /**
   * Support-Zugang des Betreibers (docs/anmeldung/SPEZIFIKATION.md §18) – „Support (Betreiber)“. Rollen, Zustand,
   * Kennwort und Zwei-Faktor verwaltet kein Betrieb; erlauben oder sperren unter Einstellungen › Sicherheit.
   */
  support: z.boolean(),
})

export const zInvitation = z.object({
  id: zId,
  email: z.string(),
  displayName: z.string(),
  status: z.enum(['pending', 'expired', 'accepted', 'revoked']),
  statusLabel: z.string(),
  expiresAt: zTimestamp,
  createdAt: zTimestamp,
  invitedByName: z.string().nullable(),
  roles: z.array(z.object({ roleId: zId, roleName: z.string(), locationId: zId.nullable(), locationLabel: z.string() })),
  /** offen oder abgelaufen: lässt sich mit neuem Link erneut senden */
  resendable: z.boolean(),
})

export const zLocationRef = z.object({ id: zId, code: z.string(), name: z.string(), kind: z.string() })

export const userList = defineContract({
  name: 'platform.user.list',
  title: 'Personen anzeigen',
  description: 'Alle Mitglieder des Betriebs mit Rollen je Standort, letzter Anmeldung und Zustand – dazu die Einladungen.',
  llm: {
    description:
      'Listet die Mitglieder des Betriebs: Name, E-Mail, Rollen je Standort, letzte Anmeldung, Zustand (aktiv, gesperrt), ' +
      'dazu offene Einladungen. Verwenden für Fragen wie „Wer arbeitet in Ismaning?“ oder „Welche Rolle hat Lea?“. ' +
      'Für die einzelnen Rechte einer Person platform.user.rights verwenden.',
    keywords: ['Mitarbeiter', 'Benutzer', 'Personen', 'Zugang', 'Rolle', 'Einladung', 'wer arbeitet'],
  },
  input: z.strictObject({
    /** auch technische Zugänge (Schnittstellen) nennen */
    includeTechnical: z.boolean().default(false),
  }),
  output: z.object({
    members: z.array(zMember),
    invitations: z.array(zInvitation),
    locations: z.array(zLocationRef),
    /** Rollen zur Auswahl beim Einladen und Zuweisen; `assignable`: Die angemeldete Person hält alles, was die Rolle gewährt */
    roleOptions: z.array(z.object({ id: zId, name: z.string(), description: z.string().nullable(), assignable: z.boolean(), allRights: z.boolean() })),
    /** Links zum Weitergeben erlaubt (Einladung, Kennwort-Link) – sonst geht alles per E-Mail an die Person */
    handoverLinks: z.boolean(),
    /** Demo-Betrieb: Kennwort-Links und Zwei-Faktor gibt es nicht */
    demo: z.boolean(),
  }),
  permission: 'platform.user.read',
  risk: 'read',
})

/** Zustellung von Einladung und Kennwort-Link (§5.5, §6.3): per E-Mail an die Person oder als Link zum Weitergeben */
export const LINK_DELIVERIES = ['email', 'link'] as const
export type LinkDeliveryChoice = (typeof LINK_DELIVERIES)[number]

/** Was mit der E-Mail einer Verwaltungsaktion geschah (vorgemerkt, gesammelt ohne Versandweg, gesperrt) */
const zQueuedMail = z.object({
  mailId: zId,
  status: z.enum(['queued', 'held', 'suppressed']),
  /** Adresse der Person */
  to: z.string(),
})

const zInvitationOutput = z.object({
  invitationId: zId,
  delivery: z.enum(LINK_DELIVERIES),
  /** nur bei `link`: Pfad des Einladungslinks ('/invitation#token=…') – erscheint nur in dieser Antwort */
  acceptPath: z.string().nullable(),
  expiresAt: zTimestamp,
  /** nur bei `email`: die vorgemerkte Nachricht */
  mail: zQueuedMail.nullable(),
})

export const userInvite = defineContract({
  name: 'platform.user.invite',
  title: 'Person einladen',
  description:
    'Stellt eine Einladung mit Rollen je Standort aus. Der Link geht per E-Mail direkt an die eingeladene Person; als Link zum Weitergeben nur, wenn der Betrieb das erlaubt.',
  llm: { description: 'Stellt eine Einladung aus. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    email: z.string().trim().min(3, { error: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' }).max(320),
    displayName: z.string().trim().min(2, { error: 'Bitte geben Sie den Namen der Person ein.' }).max(120),
    roles: z.array(zRoleGrant).min(1, { error: 'Bitte wählen Sie mindestens eine Rolle.' }).max(40),
    defaultLocationId: zId.nullish(),
    /** Gültigkeit des Links in Tagen */
    validDays: z.int().min(1, { error: 'Eine Einladung gilt mindestens einen Tag.' }).max(90, { error: 'Eine Einladung gilt höchstens 90 Tage.' }).default(14),
    /** `email` (Standard): an die eingeladene Adresse · `link`: Link einmal anzeigen (nur mit Übergabe-Links) */
    delivery: z.enum(LINK_DELIVERIES).default('email'),
  }),
  output: zInvitationOutput,
  permission: 'platform.user.manage',
  risk: 'write',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

export const userResendInvitation = defineContract({
  name: 'platform.user.resendInvitation',
  title: 'Einladung erneut senden',
  description: 'Schickt eine offene oder abgelaufene Einladung mit neuem Link noch einmal – der alte Link gilt danach nicht mehr.',
  llm: { description: 'Sendet eine Einladung erneut. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    invitationId: zId,
    delivery: z.enum(LINK_DELIVERIES).default('email'),
    validDays: z.int().min(1).max(90).default(14),
  }),
  output: zInvitationOutput,
  permission: 'platform.user.manage',
  risk: 'write',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

export const userEndSessions = defineContract({
  name: 'platform.user.endSessions',
  title: 'Sitzungen einer Person beenden',
  description:
    'Meldet eine Person in diesem Betrieb auf allen Geräten ab – sofort, auch geöffnete Fenster. Nur, wenn Sie alle Rechte der Person selbst haben; für sich selbst gibt es das Profil.',
  llm: { description: 'Beendet die Sitzungen einer Person. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ membershipId: zId }),
  output: z.object({ endedSessions: z.int().nonnegative() }),
  permission: 'platform.user.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const userResetMfa = defineContract({
  name: 'platform.user.resetMfa',
  title: 'Zwei-Faktor-Anmeldung zurücksetzen',
  description:
    'Für eine Person, die ihr Telefon und ihre Wiederherstellungscodes verloren hat: Die Zwei-Faktor-Anmeldung wird gelöscht, alle Sitzungen enden, die Person erhält einen Hinweis per E-Mail und richtet sie neu ein. Nur für Personen ohne Zugang zu einem anderen Betrieb.',
  llm: { description: 'Setzt die Zwei-Faktor-Anmeldung zurück. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    membershipId: zId,
    reason: z.string().trim().min(3, { error: 'Bitte nennen Sie einen Grund (mindestens 3 Zeichen).' }).max(300),
  }),
  output: z.object({ endedSessions: z.int().nonnegative(), mail: zQueuedMail }),
  permission: 'platform.user.manage',
  risk: 'destructive',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

export const userSendPasswordReset = defineContract({
  name: 'platform.user.sendPasswordReset',
  title: 'Kennwort-Link senden',
  description:
    'Schickt der Person einen Link, mit dem sie ein neues Kennwort festlegt (gültig 30 Minuten). Als Link zum Weitergeben nur, wenn der Betrieb das erlaubt.',
  llm: { description: 'Schickt einen Kennwort-Link. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ membershipId: zId, delivery: z.enum(LINK_DELIVERIES).default('email') }),
  output: z.object({
    delivery: z.enum(LINK_DELIVERIES),
    expiresAt: zTimestamp,
    /** nur bei `link`: '/password/reset#token=…' – erscheint nur in dieser Antwort */
    resetPath: z.string().nullable(),
    mail: zQueuedMail.nullable(),
  }),
  permission: 'platform.user.manage',
  risk: 'write',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

export const userRevokeInvitation = defineContract({
  name: 'platform.user.revokeInvitation',
  title: 'Einladung zurückziehen',
  description: 'Zieht eine offene Einladung zurück – ihr Link gilt danach nicht mehr.',
  llm: { description: 'Zieht eine Einladung zurück. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ invitationId: zId }),
  output: z.object({ revoked: z.boolean() }),
  permission: 'platform.user.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const userSetStatus = defineContract({
  name: 'platform.user.setStatus',
  title: 'Zugang sperren oder freigeben',
  description:
    'Sperrt den Zugang einer Person zu diesem Betrieb (laufende Sitzungen enden sofort) oder gibt ihn wieder frei. Rollen und Verlauf bleiben erhalten.',
  llm: { description: 'Sperrt einen Zugang oder gibt ihn frei. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    membershipId: zId,
    status: z.enum(MEMBER_STATUSES),
    reason: z.string().trim().max(300).optional(),
  }),
  output: z.object({ membershipId: zId, status: z.enum(MEMBER_STATUSES) }),
  permission: 'platform.user.manage',
  risk: 'destructive',
  effects: { securitySensitive: true, reversible: true },
  assistant: 'never',
})

export const userAssignRole = defineContract({
  name: 'platform.user.assignRole',
  title: 'Rolle zuweisen',
  description: 'Weist einer Person eine Rolle zu – für alle Standorte oder für einen. Vergeben lässt sich nur, was man selbst hält.',
  llm: { description: 'Weist einer Person eine Rolle zu. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ membershipId: zId, roleId: zId, locationId: zId.nullable() }),
  output: z.object({ assignmentId: zId, created: z.boolean() }),
  permission: 'platform.user.manage',
  risk: 'write',
  effects: { securitySensitive: true, reversible: true },
  assistant: 'never',
})

export const userUnassignRole = defineContract({
  name: 'platform.user.unassignRole',
  title: 'Rolle entziehen',
  description: 'Nimmt einer Person eine Rolle an einem Standort wieder weg. Die letzte Person mit allen Rechten behält sie.',
  llm: { description: 'Entzieht einer Person eine Rolle. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ assignmentId: zId }),
  output: z.object({ removed: z.boolean() }),
  permission: 'platform.user.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

// ---- „Was darf diese Person?“ ---------------------------------------------------------------------------------------

export const zRightLimit = z.object({
  key: z.string(),
  label: z.string(),
  unit: z.enum(['bp', 'cents', 'qty', 'count']),
  /** Höchstwert in der Einheit der Grenze; null = unbegrenzt */
  value: z.number().nullable(),
  /** „bis 50,00 €“ bzw. „unbegrenzt“ */
  text: z.string(),
})

export const zEffectiveRight = z.object({
  key: z.string(),
  /** als Handlung: „Korrekturbuchungen erfassen“ */
  label: z.string(),
  description: z.string().nullable(),
  sensitive: z.boolean(),
  /** 'all' = an allen Standorten, 'here' = am gewählten Standort, 'elsewhere' = nur an anderen Standorten */
  where: z.enum(['all', 'here', 'elsewhere']),
  /** in Worten: „an allen Standorten“, „nur F01 Getränkemarkt Garching“ */
  whereText: z.string(),
  limits: z.array(zRightLimit),
})

export const zAssistantAction = z.object({
  name: z.string(),
  title: z.string(),
  risk: z.enum(['read', 'write', 'destructive', 'financial']),
  /** nur wegen „Erweiterte Aktionen“ angeboten */
  advanced: z.boolean(),
})

export const userRights = defineContract({
  name: 'platform.user.rights',
  title: 'Was darf diese Person?',
  description:
    'Die wirksamen Rechte einer Person in Worten – je Modul, mit Grenzen – und die Aktionen, die der KI-Assistent ihr anbieten würde.',
  llm: {
    description:
      'Liefert die wirksamen Rechte einer Person (aus allen ihren Rollen), wahlweise für einen Standort: Rechte je Modul ' +
      'als Handlung, Grenzen (höchster Rabatt, Korrekturwert …) und die Liste der Aktionen, die der KI-Assistent dieser ' +
      'Person anbietet. Verwenden für „Was darf Lea in Garching?“ oder „Darf Emre Korrekturen buchen?“. Die Person vorher ' +
      'über platform.user.list ermitteln.',
    keywords: ['Rechte', 'darf', 'Berechtigung', 'Grenze', 'Rolle', 'was darf'],
  },
  input: z.strictObject({
    membershipId: zId,
    /** Standort, für den geantwortet wird; ohne Angabe: Überblick über alle Standorte der Person */
    locationId: zId.nullish(),
  }),
  output: z.object({
    member: z.object({ membershipId: zId, displayName: z.string(), status: z.string(), technical: z.boolean() }),
    location: zLocationRef.nullable(),
    /** Rollen der Person mit Standort */
    roles: z.array(z.object({ roleName: z.string(), locationLabel: z.string() })),
    /** Standorte, an denen die Person irgendein Recht hält */
    locations: z.array(zLocationRef),
    groups: z.array(
      z.object({
        module: z.string(),
        moduleTitle: z.string(),
        rights: z.array(zEffectiveRight),
        /** deklarierte Rechte des Moduls, die die Person nicht hat */
        missing: z.int().nonnegative(),
      }),
    ),
    assistant: z.object({
      /** die Person darf den Assistenten nutzen und der Betrieb hat ihn eingeschaltet */
      available: z.boolean(),
      /** Satz, warum nicht (Recht fehlt, Betrieb hat den Assistenten aus) */
      reason: z.string().nullable(),
      advancedActionsEnabled: z.boolean(),
      /** genau die Aktionen, die der Assistent dieser Person anbietet – nach Modul */
      groups: z.array(z.object({ module: z.string(), moduleTitle: z.string(), actions: z.array(zAssistantAction) })),
      total: z.int().nonnegative(),
      /** erlaubte Aktionen, die dem Assistenten grundsätzlich vorenthalten sind (Verwaltung, reine Oberfläche) */
      withheld: z.int().nonnegative(),
    }),
  }),
  permission: 'platform.role.read',
  risk: 'read',
})

/** Was „Erweiterte Aktionen“ für den Assistenten freigibt und was ihm grundsätzlich vorenthalten ist – für die Einstellungen. */
export const assistantActionCatalog = defineContract({
  name: 'platform.assistantActions.list',
  title: 'Aktionen des Assistenten: Katalog',
  description:
    'Welche Aktionen der Assistent erst mit „Erweiterte Aktionen“ anbietet und wie viele ihm grundsätzlich vorenthalten sind – unabhängig von den Rechten einer Person.',
  llm: { description: 'Katalog für die Einstellungen des Assistenten. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    /** Verträge mit assistant: 'opt-in', nach Modul */
    advanced: z.array(z.object({ module: z.string(), moduleTitle: z.string(), actions: z.array(zAssistantAction) })),
    advancedTotal: z.int().nonnegative(),
    /** Aktionen aktiver Module, die der Assistent ohne den Schalter anbieten kann */
    standardTotal: z.int().nonnegative(),
    /** Aktionen, die dem Assistenten nie angeboten werden (Verwaltung, reine Oberfläche) */
    neverTotal: z.int().nonnegative(),
  }),
  permission: '@authenticated',
  risk: 'read',
  assistant: 'never',
})
